import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, RotateCcw, Loader2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import {
  deleteExpense,
  expenseActivity,
  getExpense,
  listCategories,
  listPrograms,
  listStreams,
  restoreExpense,
  updateExpense,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import StatusPill from "@/components/StatusPill";
import { ACTIVITY } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function money(n, c = "USD") { return `${c === "USD" ? "$" : c + " "}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

const ACTION_LABELS = {
  created: "submitted the expense",
  updated: "edited the expense",
  approved: "approved",
  rejected: "rejected",
  info_requested: "requested more info",
  reimbursed: "marked reimbursed",
  deleted: "deleted",
  restored: "restored",
};

export default function ExpenseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [expense, setExpense] = useState(null);
  const [activity, setActivity] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [streams, setStreams] = useState([]);
  const [editing, setEditing] = useState(false);
  const [working, setWorking] = useState(false);

  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [spentOn, setSpentOn] = useState("");
  const [programId, setProgramId] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [streamId, setStreamId] = useState("");

  const load = useCallback(async () => {
    const [e, a, p, c, s] = await Promise.all([getExpense(id), expenseActivity(id), listPrograms(), listCategories(), listStreams()]);
    setExpense(e);
    setActivity(a);
    setPrograms(p);
    setCategories(c);
    setStreams(s);
    setVendor(e.vendor); setAmount(String(e.amount)); setCurrency(e.currency); setSpentOn(e.spent_on);
    setProgramId(e.program_id); setCategory(e.category); setNotes(e.notes || ""); setStreamId(e.stream_id || "");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!expense) return null;

  const isOwner = expense.submitted_by === user.id;
  const canEdit =
    (user.role === "admin" || user.role === "treasurer") ||
    (user.role === "volunteer" && isOwner && ["pending", "info_requested"].includes(expense.status));
  const canDelete = canEdit && !expense.deleted_at;
  const canRestore = (user.role === "admin" || user.role === "treasurer") && !!expense.deleted_at;

  const save = async () => {
    setWorking(true);
    try {
      const payload = {
        vendor, amount: parseFloat(amount), currency: currency.toUpperCase(), spent_on: spentOn,
        program_id: programId, category, notes: notes || null, stream_id: streamId || null,
      };
      const updated = await updateExpense(id, payload);
      setExpense(updated);
      setEditing(false);
      toast({ title: "Saved" });
      const a = await expenseActivity(id);
      setActivity(a);
    } catch (err) {
      toast({ title: "Could not save", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this expense? You can restore it within the trash.")) return;
    try {
      await deleteExpense(id);
      toast({ title: "Deleted" });
      navigate("/app/expenses");
    } catch (err) {
      toast({ title: "Could not delete", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const restore = async () => {
    try {
      await restoreExpense(id);
      toast({ title: "Restored" });
      await load();
    } catch (err) {
      toast({ title: "Could not restore", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  return (
    <>
      <PageHeader title={expense.vendor} subtitle={`${expense.program_name} · ${expense.category}`}>
        <Link to="/app/expenses" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> All expenses
        </Link>
      </PageHeader>
      <PageBody>
        {expense.deleted_at && (
          <div className="mb-6 rounded-md border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 text-sm flex items-center justify-between">
            <span>This expense was deleted.</span>
            {canRestore && (
              <button onClick={restore} className="inline-flex items-center gap-1 text-xs underline">
                <RotateCcw size={12} /> Restore
              </button>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-6">
            {/* Summary card */}
            <div className="border border-border bg-card rounded-md p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <StatusPill status={expense.status} />
                  <div className="serif text-3xl mt-3 text-primary tabular mono">{money(expense.amount, expense.currency)}</div>
                  <div className="text-xs text-muted-foreground mono mt-1">{expense.spent_on}</div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && !editing && (
                    <button onClick={() => setEditing(true)} className="rounded-md border border-border bg-background hover:bg-accent px-3 py-1.5 text-xs">Edit</button>
                  )}
                  {canDelete && !editing && (
                    <button onClick={remove} className="rounded-md border border-destructive/30 text-destructive hover:bg-destructive/5 px-3 py-1.5 text-xs inline-flex items-center gap-1">
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                </div>
              </div>

              {editing ? (
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <Field label="Vendor"><input value={vendor} onChange={(e) => setVendor(e.target.value)} className="td-input" /></Field>
                  <Field label="Date"><input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} className="td-input" /></Field>
                  <Field label="Amount"><input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="td-input mono" /></Field>
                  <Field label="Currency"><input maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="td-input mono" /></Field>
                  <Field label="Program">
                    <Select value={programId} onValueChange={setProgramId}>
                      <SelectTrigger className="td-input h-auto py-2.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Category">
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="td-input h-auto py-2.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Grant / Fund stream">
                    <Select value={streamId || "_none"} onValueChange={(v) => setStreamId(v === "_none" ? "" : v)}>
                      <SelectTrigger className="td-input h-auto py-2.5"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {streams.map((s) => <SelectItem key={s.id} value={s.id}>{s.kind === "grant" ? "Grant · " : "Fund · "}{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Notes"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="td-input" /></Field>
                  <div className="sm:col-span-2 flex justify-end gap-2">
                    <button onClick={() => setEditing(false)} className="rounded-md border border-border bg-background hover:bg-accent px-4 py-2 text-sm">Cancel</button>
                    <button onClick={save} disabled={working} className="rounded-md bg-primary text-primary-foreground px-5 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
                      {working ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  <KV label="Program" value={expense.program_name} />
                  <KV label="Category" value={expense.category} />
                  <KV label="Submitted by" value={expense.submitted_by_name} />
                  <KV label="Submitted at" value={new Date(expense.submitted_at).toLocaleString()} />
                  {expense.stream_name && <KV label="Linked stream" value={expense.stream_name} />}
                  {expense.decision_note && <KV label="Decision note" value={expense.decision_note} full />}
                  {expense.notes && <KV label="Notes" value={expense.notes} full />}
                  {expense.payment_method && <KV label="Payment" value={`${expense.payment_method} · ${expense.payment_reference || ""}`} full />}
                </div>
              )}
            </div>

            {expense.receipt_data_url && (
              <div className="border border-border bg-card rounded-md overflow-hidden">
                <div className="px-6 py-3 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">Receipt</div>
                <img src={expense.receipt_data_url} alt="Receipt" className="w-full max-h-[600px] object-contain bg-muted" />
              </div>
            )}
          </div>

          {/* Activity log */}
          <div className="lg:col-span-5">
            <div data-testid={ACTIVITY.list(expense.id)} className="border border-border bg-card rounded-md p-6">
              <div className="serif text-lg text-primary">Activity</div>
              <div className="text-xs text-muted-foreground mt-0.5">Every change, every approver.</div>
              <ol className="mt-6 relative border-l border-border ml-2">
                {activity.map((a) => (
                  <li key={a.id} data-testid={ACTIVITY.entry(a.id)} className="ml-5 pb-6">
                    <div className="absolute -left-1.5 w-3 h-3 rounded-full" style={{ background: bullet(a.action) }} />
                    <div className="text-sm">
                      <span className="font-medium">{a.actor_name}</span>{" "}
                      <span className="text-muted-foreground">{ACTION_LABELS[a.action] || a.action}</span>
                    </div>
                    {a.note && <div className="text-xs text-muted-foreground mt-1 italic">"{a.note}"</div>}
                    {a.diff && (
                      <ul className="mt-2 text-[11px] text-muted-foreground mono space-y-0.5">
                        {Object.entries(a.diff).map(([k, [b, n]]) => (
                          <li key={k}>
                            <span className="text-foreground">{k}</span>: {fmt(b)} → {fmt(n)}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="text-[11px] text-muted-foreground mono mt-1.5">{new Date(a.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function KV({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}
function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toString();
  return String(v).slice(0, 40);
}
function bullet(action) {
  const map = {
    created: "hsl(225 45% 20%)",
    updated: "hsl(45 85% 50%)",
    approved: "hsl(145 65% 35%)",
    rejected: "hsl(0 70% 45%)",
    info_requested: "hsl(220 25% 50%)",
    reimbursed: "hsl(225 45% 20%)",
    deleted: "hsl(0 70% 45%)",
    restored: "hsl(145 65% 35%)",
  };
  return map[action] || "hsl(220 20% 60%)";
}
